import { useCallback, useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';

import { ListingApplicationPayload } from 'api/ats';
import { CmsSubscribe } from 'api/cms';
import { SalaryReportFormData } from 'api/compensationDatabase';
import { del, getJson, postJson } from 'utils/http';
import { removeParam } from 'utils/url';

type TmpListingApplicationPayload = {
  initialValues: ListingApplicationPayload;
};
type SavedSearchUnsaved = {
  savedSearch: {
    text: string;
    filters: Record<string, object>;
    location: SearchLocation;
    radius: number | null | undefined;
    index: 'MAIN' | 'ACTIONS';
  };
};
type SignupBlockRedirect = {
  redirectUrl: string;
};
export type SaveListingType = {
  listing: {
    id: string;
    type: string;
    name: string;
  };
};
export type SubscribeSubsiteType = {
  subscribedSubsiteId: string;
};
export type SurveyReportDataType = {
  surveyReport: {
    initialValues: SalaryReportFormData;
  };
};

type StateType =
  | object
  | TmpListingApplicationPayload
  | SavedSearchUnsaved
  | CmsSubscribe
  | SignupBlockRedirect
  | SaveListingType
  | SubscribeSubsiteType
  | SurveyReportDataType;

type ActionType =
  | 'CMS_SUBSCRIBE'
  | 'SURVEY_REPORT_DATA'
  | 'LISTING_APPLICATION_VALUES'
  | 'SAVE_SEARCH'
  | 'SAVE_LISTING'
  | 'SIGNUP_BLOCK_REDIRECT'
  | 'SUBSCRIBE_SUBSITE';
type ListingApplicationValuesAction = {
  type: 'LISTING_APPLICATION_VALUES';
  data: TmpListingApplicationPayload;
};
type SaveSearchAction = {
  type: 'SAVE_SEARCH';
  data: SavedSearchUnsaved;
};
type CmsSubscribeAction = {
  type: 'CMS_SUBSCRIBE';
  data: CmsSubscribe;
};
type SignupBlockRedirectAction = {
  type: 'SIGNUP_BLOCK_REDIRECT';
  data: SignupBlockRedirect;
};
type SaveListingAction = {
  type: 'SAVE_LISTING';
  data: SaveListingType;
};
type SusbscribeSubsiteAction = {
  type: 'SUBSCRIBE_SUBSITE';
  data: SubscribeSubsiteType;
};
type SurveyReportDataAction = {
  type: 'SURVEY_REPORT_DATA';
  data: SurveyReportDataType;
};

type Action =
  | ListingApplicationValuesAction
  | SaveSearchAction
  | CmsSubscribeAction
  | SignupBlockRedirectAction
  | SaveListingAction
  | SusbscribeSubsiteAction
  | SurveyReportDataAction;

const mapStateToTemporaryStorage = <T>(
  state: Partial<Record<ActionType, T>>
): Array<{
  type: ActionType;
  data: T;
}> =>
  Object.keys(state).map((key: ActionType) => ({
    type: key,
    data: state[key],
  }));

export const saveTemporaryStorage = <T>(
  state: Partial<Record<ActionType, T>>
) =>
  postJson('/data/tmp-storage', {
    data: JSON.stringify(mapStateToTemporaryStorage(state)),
  }).then(({ data: { id } }) => id);

// eslint-disable-next-line import/no-default-export
export default function useTemporaryStorage<T extends StateType>(
  tmpIdArg: string | null | undefined,
  skipRequiredInfo?: boolean
): [
  (arg0: Partial<Record<ActionType, StateType>>) => Promise<string>,
  StateType,
  boolean
] {
  const [loaded, setLoaded] = useState(false);
  const [tmpId, setTmpId] = useState(null);
  const { isLoggedIn, hasRequiredInfo } = useSelector((state) => ({
    isLoggedIn:
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentUser' does not exist on type 'Def... Remove this comment to see the full error message
      state.currentUser &&
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentUser' does not exist on type 'Def... Remove this comment to see the full error message
      state.currentUser.id &&
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentUser' does not exist on type 'Def... Remove this comment to see the full error message
      (state.currentUser.emailVerified || skipRequiredInfo),
    hasRequiredInfo: !!(
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentUser' does not exist on type 'Def... Remove this comment to see the full error message
      (state.currentUser.firstName && state.currentUser.lastName)
    ),
  }));
  useEffect(() => {
    if (tmpIdArg && !tmpId && !loaded) {
      setTmpId(tmpIdArg);
    }
  }, [tmpIdArg, loaded, tmpId]);
  const memoizedReducer = useCallback(
    //    (state: Partial<Record<ActionType, T>>, action: Action): T => {
    (state: StateType, action: Action): StateType => {
      switch (action.type) {
        case 'LISTING_APPLICATION_VALUES': {
          return {
            ...state,
            initialValues: {
              id: action.data.initialValues.id,
              firstName: action.data.initialValues.firstName,
              lastName: action.data.initialValues.lastName,
              email: action.data.initialValues.email,
              requestedAttachments:
                action.data.initialValues.requestedAttachments,
              resumeS3FileId: action.data.initialValues.resumeS3FileId,
              type: action.data.initialValues.type,
              consentAts: action.data.initialValues.consentAts,
            },
          };
        }

        case 'SAVE_SEARCH':
          return { ...state, savedSearch: { ...action.data.savedSearch } };

        // return reduxDispatch(saveSearch(action.data));
        case 'CMS_SUBSCRIBE': {
          return {
            ...state,
            blockId: action.data.blockId,
            index: action.data.index,
          };
        }

        case 'SIGNUP_BLOCK_REDIRECT': {
          return { ...state, redirectUrl: action.data.redirectUrl };
        }

        case 'SAVE_LISTING': {
          return { ...state, listing: action.data.listing };
        }

        case 'SUBSCRIBE_SUBSITE': {
          return {
            ...state,
            subscribedSubsiteId: action.data.subscribedSubsiteId,
          };
        }

        case 'SURVEY_REPORT_DATA': {
          return {
            ...state,
            surveyReport: {
              initialValues: action.data.surveyReport.initialValues,
            },
          };
        }

        default:
          throw new Error(
            // @ts-expect-error -- type not in never error
            `Undefined temporary storage key ${action && action.type}`
          );
      }
    },
    []
  );
  const [temporaryStorage, dispatch] = useReducer(memoizedReducer, {} as T);
  useEffect(() => {
    const mapTemporaryStorageToState = (
      serializedTemporaryStorage: string | null | undefined
    ) => {
      if (!serializedTemporaryStorage) return null;
      return JSON.parse(serializedTemporaryStorage).forEach((action: Action) =>
        dispatch(action)
      );
    };

    const deleteTemporaryStorage = (temporaryStorageId: string) =>
      del(`/data/tmp-storage/${temporaryStorageId}`);

    const getTemporaryStorage = (temporaryStorageId: string) =>
      getJson(`/data/tmp-storage/${temporaryStorageId}`).then(({ data }) => {
        setLoaded(true);
        setTmpId(null);
        deleteTemporaryStorage(temporaryStorageId);
        const newUrl = `${window.location.pathname}?${removeParam(
          window.location.search.replace('?', ''),
          'tmpId'
        )}`;
        window.history.replaceState(
          {
            path: newUrl,
          },
          '',
          newUrl
        );
        return mapTemporaryStorageToState(data);
      });

    if (
      tmpId &&
      !loaded &&
      isLoggedIn &&
      (hasRequiredInfo || skipRequiredInfo)
    ) {
      getTemporaryStorage(tmpId);
    }
  }, [
    hasRequiredInfo,
    isLoggedIn,
    loaded,
    setLoaded,
    skipRequiredInfo,
    temporaryStorage,
    tmpId,
  ]);
  return [saveTemporaryStorage, temporaryStorage, loaded];
}
